// src/components/ClientViewModal.jsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Row, Col, Badge } from 'react-bootstrap';
import clientMinorService from '../services/clientMinorService';
import ClientChildrenListModal from './ClientChildrenListModal';
import ViewPdfButton from './ViewPdfButton';

function FieldRow({ label, value, code, after }) {
  return (
    <tr>
      <th style={{ width: 180 }}>{label}</th>
      <td>
        {value ?? '—'}
        {code ? (
          <Badge bg="light" text="dark" className="ms-2">
            {code}
          </Badge>
        ) : null}
        {after ? <span className="ms-2">{after}</span> : null}
      </td>
    </tr>
  );
}

export default function ClientViewModal({ show, onHide, client }) {
  const [children, setChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [showChildrenModal, setShowChildrenModal] = useState(false);

  useEffect(() => {
    if (!show || !client?.id) return;
    setLoadingChildren(true);
    clientMinorService
      .listByParent(client.id)
      .then(setChildren)
      .finally(() => setLoadingChildren(false));
  }, [show, client?.id]);

  if (!client) return null;

  const {
    firstName,
    lastName,
    nifCin,
    email,
    phone,
    isMinor,
    tutorName,
    tutorRelation,
    extra,
    active,
  } = client;

  const childrenCount = children.length;

  const getDocument = () => ({
    title: `Fiche client${firstName || lastName ? ` - ${firstName || ''} ${lastName || ''}` : ''}`.trim(),
    sections: [
      {
        title: 'Informations principales',
        rows: [
          { label: 'Prénom', value: firstName || '—' },
          { label: 'Nom', value: lastName || '—' },
          { label: 'NIF/CIN', value: nifCin || '—' },
          { label: 'Email', value: email || '—' },
          { label: 'Téléphone', value: phone || '—' },
          { label: 'Statut', value: active ? 'Actif' : 'Désactivé' },
          { label: 'Mineur', value: isMinor ? 'Oui' : 'Non' },
          { label: 'Tuteur', value: tutorName || '—' },
          { label: 'Lien tuteur', value: tutorRelation || '—' },
          {
            label: 'Enfants',
            value: loadingChildren
              ? 'Chargement…'
              : childrenCount === 0
              ? '0'
              : String(childrenCount),
          },
        ],
      },
      ...(extra && Object.keys(extra).length > 0
        ? [
            {
              title: 'Champs supplémentaires',
              rows: Object.entries(extra).map(([code, val]) => ({
                label: code,
                value: val ?? '—',
              })),
            },
          ]
        : []),
    ],
  });

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Voir — Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <h6>Informations principales</h6>
              <Table bordered size="sm">
                <tbody>
                  <FieldRow label="Prénom" value={firstName} />
                  <FieldRow label="Nom" value={lastName} />
                  <FieldRow label="NIF/CIN" value={nifCin || '—'} />
                  <FieldRow label="Email" value={email || '—'} />
                  <FieldRow label="Téléphone" value={phone || '—'} />
                  <FieldRow
                    label="Statut"
                    value={active ? 'Actif' : 'Désactivé'}
                    after={
                      active ? (
                        <Badge bg="success">Actif</Badge>
                      ) : (
                        <Badge bg="danger">Désactivé</Badge>
                      )
                    }
                  />
                  <FieldRow label="Mineur" value={isMinor ? 'Oui' : 'Non'} />
                  {tutorName ? <FieldRow label="Tuteur" value={tutorName} /> : null}
                  {tutorRelation ? (
                    <FieldRow label="Lien tuteur" value={tutorRelation} />
                  ) : null}

                  <FieldRow
                    label="Enfants"
                    value={
                      loadingChildren
                        ? 'Chargement…'
                        : childrenCount === 0
                        ? '0'
                        : `${childrenCount}`
                    }
                    after={
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={() => setShowChildrenModal(true)}
                        disabled={loadingChildren}
                      >
                        Voir la liste
                      </Button>
                    }
                  />
                </tbody>
              </Table>
            </Col>

            {extra && Object.keys(extra).length > 0 && (
              <Col md={12}>
                <h6 className="mt-2">Champs supplémentaires</h6>
                <Table bordered size="sm">
                  <tbody>
                    {Object.entries(extra).map(([code, val]) => (
                      <FieldRow key={code} label={code} value={val} code={code} />
                    ))}
                  </tbody>
                </Table>
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <ViewPdfButton getDocument={getDocument} className="me-2" />
          <Button variant="secondary" onClick={onHide}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      {showChildrenModal && (
        <ClientChildrenListModal
          show={true}
          onHide={() => setShowChildrenModal(false)}
          parent={client}
          initialItems={children}
        />
      )}
    </>
  );
}